import { useState, useEffect, useCallback } from "react";

const DEFAULT_SHORTCUTS = {
  // Zoom shortcuts
  ZOOM_IN: { key: "=", ctrlKey: true },
  ZOOM_OUT: { key: "-", ctrlKey: true },
  ZOOM_FIT: { key: "0", ctrlKey: true },

  // Selection
  ESCAPE: { key: "Escape" },

  // for playback control
  PLAY_PAUSE: { key: " " }, // Spacebar for play/pause

  // Undo/Redo shortcuts
  UNDO: { key: "z", ctrlKey: true },
  REDO: { key: "y", ctrlKey: true },
  REDO_ALT: { key: "z", ctrlKey: true, shiftKey: true }, // Alternative: Ctrl+Shift+Z

  // Delete
  DELETE: { key: "Delete" },
  DELETE_ALT: { key: "Backspace" },

  // Copy/Paste
  COPY: { key: "c", ctrlKey: true },
  PASTE: { key: "v", ctrlKey: true },
  CUT: { key: "x", ctrlKey: true },

  // Select All
  SELECT_ALL: { key: "a", ctrlKey: true },

  //MOVING SPRITE
  MOVE_UP: { key: "ArrowUp", payload: { x: 0, y: -10 } },
  MOVE_DOWN: { key: "ArrowDown", payload: { x: 0, y: 10 } },
  MOVE_LEFT: { key: "ArrowLeft", payload: { x: -10, y: 0 } },
  MOVE_RIGHT: { key: "ArrowRight", payload: { x: 10, y: 0 } },

  MOVE_UP_FAST: { key: "ArrowUp", shiftKey: true, payload: { x: 0, y: -50 } },
  MOVE_DOWN_FAST: {
    key: "ArrowDown",
    shiftKey: true,
    payload: { x: 0, y: 50 },
  },
  MOVE_LEFT_FAST: {
    key: "ArrowLeft",
    shiftKey: true,
    payload: { x: -50, y: 0 },
  },
  MOVE_RIGHT_FAST: {
    key: "ArrowRight",
    shiftKey: true,
    payload: { x: 50, y: 0 },
  },
};

export const useKeyboardShortcuts = (
  shortcuts = DEFAULT_SHORTCUTS,
  callbacks = {},
  options = {}
) => {
  const [pressedKeys, setPressedKeys] = useState(new Set());
  const [activeShortcuts, setActiveShortcuts] = useState(new Set());

  const {
    enabled = true,
    preventDefault = true,
    targetRef = null, // Reference to element that should be focused
  } = options;

  // Check if shortcut should be executed
  const shouldExecuteShortcut = useCallback(() => {
    if (!enabled) return false;

    // If we have a target ref, check if it's focused
    if (targetRef?.current) {
      const isTargetFocused =
        document.activeElement === targetRef.current ||
        targetRef.current.contains(document.activeElement);
      return isTargetFocused;
    }

    // If no target ref, avoid executing when typing in inputs
    const activeElement = document.activeElement;
    const isTypingInInput =
      activeElement &&
      (activeElement.tagName === "INPUT" ||
        activeElement.tagName === "TEXTAREA" ||
        activeElement.contentEditable === "true");

    return !isTypingInInput;
  }, [enabled, targetRef]);

  // Check if a shortcut matches the current key state
  const matchesShortcut = useCallback((shortcut, event) => {
    const keyMatches = shortcut.key === event.key;
    const ctrlMatches = shortcut.ctrlKey ? event.ctrlKey : !event.ctrlKey;
    const altMatches = shortcut.altKey ? event.altKey : !event.altKey;
    const shiftMatches = shortcut.shiftKey ? event.shiftKey : !event.shiftKey;
    const metaMatches = shortcut.metaKey ? event.metaKey : !event.metaKey;

    return (
      keyMatches && ctrlMatches && altMatches && shiftMatches && metaMatches
    );
  }, []);

  // Handle keydown events
  const handleKeyDown = useCallback(
    (event) => {
      if (!shouldExecuteShortcut()) return;

      // Update pressed keys
      setPressedKeys((prev) => new Set([...prev, event.key]));

      // Check for shortcut matches
      Object.entries(shortcuts).forEach(([shortcutName, shortcutConfig]) => {
        if (matchesShortcut(shortcutConfig, event)) {
          // Prevent default if configured
          if (shortcutConfig.preventDefault || preventDefault) {
            event.preventDefault();
          }

          // Add to active shortcuts
          setActiveShortcuts((prev) => new Set([...prev, shortcutName]));

          // Execute callback if provided
          if (callbacks[shortcutName]) {
            callbacks[shortcutName](event, shortcutName, shortcutConfig);
          }
        }
      });
    },
    [
      shouldExecuteShortcut,
      shortcuts,
      callbacks,
      preventDefault,
      matchesShortcut,
    ]
  );

  // Handle keyup events
  const handleKeyUp = useCallback(
    (event) => {
      if (!shouldExecuteShortcut()) return;

      // Update pressed keys
      setPressedKeys((prev) => {
        const newSet = new Set(prev);
        newSet.delete(event.key);
        return newSet;
      });

      // Remove from active shortcuts
      Object.entries(shortcuts).forEach(([shortcutName, shortcutConfig]) => {
        if (shortcutConfig.key === event.key) {
          setActiveShortcuts((prev) => {
            const newSet = new Set(prev);
            newSet.delete(shortcutName);
            return newSet;
          });

          // Execute keyup callback if provided
          const keyupCallbackName = `${shortcutName}_UP`;
          if (callbacks[keyupCallbackName]) {
            callbacks[keyupCallbackName](event, shortcutName, shortcutConfig);
          }
        }
      });
    },
    [shouldExecuteShortcut, shortcuts, callbacks]
  );

  // Set up event listeners
  useEffect(() => {
    if (!enabled) return;

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, [enabled, handleKeyDown, handleKeyUp]);

  // Utility functions
  const isShortcutActive = useCallback(
    (shortcutName) => {
      return activeShortcuts.has(shortcutName);
    },
    [activeShortcuts]
  );

  const isKeyPressed = useCallback(
    (keyCode) => {
      return pressedKeys.has(keyCode);
    },
    [pressedKeys]
  );

  return {
    // State
    pressedKeys: Array.from(pressedKeys),
    activeShortcuts: Array.from(activeShortcuts),

    // Utilities
    isShortcutActive,
    isKeyPressed,

    // Configuration
    shortcuts,
  };
};
